import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, switchMap, take, timeout } from 'rxjs/operators';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { UserService } from 'app/core/user/user.service';
import { User } from 'app/core/user/user.types';
import HmacSHA256 from 'crypto-js/hmac-sha256';
import Base64 from 'crypto-js/enc-base64';
import { AudioPlayerService } from 'app/shared/components/audio-player/audio-player.service';

@Injectable()
export class AuthService {
    private _authenticated: boolean = false;

    constructor(
        private _httpClient: HttpClient,
        private _userService: UserService,
        private _audioPlayerService: AudioPlayerService
    ) {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for access token
     */
    set accessToken(token: string) {
        localStorage.setItem('accessToken', token);
    }

    get accessToken(): string {
        return localStorage.getItem('accessToken') ?? '';
    }

    set adminAccessToken(token: string | undefined) {
        if (!token) {
            localStorage.removeItem('adminAccessToken');
            return;
        }
        localStorage.setItem('adminAccessToken', token);
    }

    get adminAccessToken(): string {
        return localStorage.getItem('adminAccessToken') ?? '';
    }

    get hasAdminAccess(): boolean {
        return !!this.adminAccessToken;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Forgot password
     *
     * @param email
     */
    forgotPassword(email: string): Observable<any> {
        return this._httpClient.post('api/auth/forgot-password', { email: email });
    }

    /**
     * Reset password
     *
     * @param password
     */
    resetPassword(password: string, token: string): Observable<any> {
        return this._httpClient.post('api/auth/reset-password', { token: token, password: password });
    }

    verifyEmail(uuid: string) {
        return this._httpClient.post<User>('api/auth/verify/' + uuid, {}).pipe(
            switchMap((response: any) => {
                this.adminAccessToken = undefined;
                this._userService.adminUser = undefined;
                this.accessToken = response.accessToken;
                this._authenticated = true;
                this._userService.user = response.user;
                return of(response.user);
            })
        );
    }

    requestNewVerificationCode() {
        return this._httpClient.post<void>('api/auth/request-new-verification', {});
    }

    /**
     * Sign in
     *
     * @param credentials
     */
    signIn(credentials: { email: string; password: string }): Observable<any> {
        return this._httpClient.post('api/auth/sign-in', credentials).pipe(
            switchMap((response: any) => {
                this._audioPlayerService.reset();
                this.adminAccessToken = undefined;
                this._userService.adminUser = undefined;
                this.accessToken = response.accessToken;
                this._authenticated = true;
                this._userService.user = response.user;
                return of(response);
            })
        );
    }

    signInAsUser(user: User): Observable<any> {
        return this._httpClient.post('api/auth/sign-in-as-user', { userId: user.uuid }).pipe(
            switchMap((response: any) => {
                this._audioPlayerService.reset();
                this.adminAccessToken = this.accessToken;
                this._userService.user$.pipe(take(1), timeout(1)).subscribe({
                    next: oldUser => this._userService.adminUser = oldUser,
                    error: err => {
                        console.error(err);
                    }
                });
                this.accessToken = response.accessToken;
                this._authenticated = true;
                this._userService.user = response.user;
                return of(response);
            })
        );
    }

    signInAsAdmin(): Observable<any> {
        this.accessToken = this.adminAccessToken;
        this._audioPlayerService.reset();
        this._userService.adminUser$.pipe(take(1), timeout(1)).subscribe({
            next: user => this._userService.user = user,
            error: err => {
                console.error(err);
                this.signOut();
            }
        });
        this.adminAccessToken = undefined;
        this._userService.adminUser = undefined;
        return of(true);
    }

    /**
     * Sign in using the access token
     */
    signInUsingToken(): Observable<any> {
        // Renew token
        return this._httpClient.post('api/auth/refresh-access-token', {
            accessToken: this.accessToken
        }).pipe(
            catchError(() =>

                // Return false
                of(false)
            ),
            switchMap((response: any) => {

                // Store the access token in the local storage
                this.accessToken = response.accessToken;

                // Set the authenticated flag to true
                this._authenticated = true;

                // Store the user on the user service
                this._userService.user = response.user;

                // Return true
                return of(true);
            })
        );
    }

    /**
     * Sign out
     */
    signOut(): Observable<any> {
        this._audioPlayerService.reset();
        this.adminAccessToken = undefined;
        this._userService.adminUser = undefined;
        this.accessToken = undefined;
        this._authenticated = false;
        return of(true);
    }

    /**
     * Sign up
     *
     * @param user
     */
    signUp(user: { name: string; email: string; password: string; company: string, notifications: boolean }): Observable<any> {
        return this._httpClient.post('api/auth/sign-up', user);
    }

    /**
     * Unlock session
     *
     * @param credentials
     */
    unlockSession(credentials: { email: string; password: string }): Observable<any> {
        return this._httpClient.post('api/auth/unlock-session', credentials);
    }

    /**
     * Check the authentication status
     */
    check(): Observable<boolean> {
        // Check if the user is logged in
        if (this._authenticated) {
            return of(true);
        }

        // Check the access token availability
        if (!this.accessToken) {
            return of(false);
        }

        // Check the access token expire date
        if (AuthUtils.isTokenExpired(this.accessToken)) {
            localStorage.removeItem('accessToken');
            this._authenticated = false;
            return of(false);
        }

        // If the access token exists and it didn't expire, sign in using it
        return this.signInUsingToken();
    }

    private static _base64urlEncode(source: any): string {
        // Encode in classical base64
        let encodedSource = Base64.stringify(source);

        // Remove padding equal characters
        encodedSource = encodedSource.replace(/=+$/, '');

        // Replace characters according to base64url specifications
        encodedSource = encodedSource.replace(/\+/g, '-');
        encodedSource = encodedSource.replace(/\//g, '_');

        // Return the base64 encoded string
        return encodedSource;
    }

    private static _base64urlDecode(source: any): any {
        // Replace characters according to base64url specifications
        source = source.replace(/-/g, '+');
        source = source.replace(/_/g, '/');

        // Return the base64 encoded string
        return JSON.parse(atob(source));
    }
}
